import React, {useState, useMemo, useEffect, useRef} from "react";
import {inject, observer} from "mobx-react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {isEqual} from "lodash";
// local
import {IconPlus} from "components/icons";
import ProjectItem from "./ProjectItem";
import {ProjectModal} from "components/modals/ProjectModal";
import {Preloader} from "components/Preloader";
import {hasPermission} from "helpers/helper";
import {ConfirmModal} from "components/ConfirmModal";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {projectsTabs, projectTabKeys, hashes} from "constants/tabs.const";
import {ProjectStartGuide} from "./ProjectStartGuide";
import {PaginationType} from "components/Pagination";

export const ProjectsTab = inject("store")(
  observer(({store: {auth, buyer, organization}}) => {
    const {user} = auth;
    const {loading, projects, projectFilterParams} = buyer;
    const {departments} = organization;
    const [isProjectModal, setIsProjectModal] = useState(null);
    const navigate = useNavigate();
    const {t} = useTranslation();
    const prevRef = useRef();

    const activeProjectsTab = useMemo(() => {
      return projectsTabs.find(
        (tab) => tab.requestKey === (projectFilterParams?.tab ?? projectTabKeys.MINE)
      )?.id;
    }, [projectFilterParams]);
    const isProjectsFull = useMemo(
      () => !!projects?.rows?.length || !!projects?.totalItems,
      [projects]
    );

    const handleClickNewProject = () =>
      hasPermission(["CREATE_PROJECT"])
        ? setIsProjectModal("new-project")
        : ConfirmModal({
            title: t("WANT_START_PROJECT"),
            text: t("CANT_START_PROJECT"),
            okText: "READ_AND_UPGRADE",
            type: "warning",
            onOk: () => navigate({hash: hashes.SUBSCRIPTION}),
          });

    const handleClose = (isUpdateProject) => {
      setIsProjectModal(null);
      if ([projectTabKeys.MINE, projectTabKeys.ALL].includes(isUpdateProject)) {
        buyer.setProjectFilterParams({...projectFilterParams, tab: isUpdateProject});
      }
      if (typeof isUpdateProject === "boolean") buyer.getProjects(isUpdateProject);
    };

    const handlePageChange = (page) =>
      buyer.setProjectFilterParams({...projectFilterParams, page});

    useEffect(() => {
      if (!isEqual(prevRef.current, projectFilterParams) && departments) {
        buyer.setProjects(null);
        buyer.getProjects(false);
        prevRef.current = projectFilterParams;
      }
    }, [projectFilterParams, departments, buyer]);

    return (
      <div className="projects-tab">
        {loading && <Preloader addClass="preloader100" />}
        {!loading && (
          <div className="projects-tab__content">
            {isProjectsFull ? (
              projects?.rows?.map((project) => (
                <ProjectItem
                  key={project.id}
                  project={project}
                  ownUser={project.Members?.map((member) => member.id).includes(user.id)}
                  editProject={setIsProjectModal}
                />
              ))
            ) : (
              <React.Fragment>
                <div className="empty-projects-description">
                  {activeProjectsTab === 2 && t("EMPTY_PROJECTS_ARCHIVED_DESCRIPTION")}
                </div>
                {activeProjectsTab !== 2 && (
                  <ButtonTransparent
                    addClass="mt-8"
                    name="CREATE_PROJECT"
                    type="button"
                    icon={<IconPlus />}
                    onClick={handleClickNewProject}
                  />
                )}
                {activeProjectsTab !== 2 && <ProjectStartGuide step={1} />}
              </React.Fragment>
            )}
          </div>
        )}
        {isProjectModal && (
          <ProjectModal projectId={isProjectModal} onClose={handleClose} />
        )}
        {projects?.totalItems > 20 && (
          <PaginationType
            currentPage={projects?.currentPage}
            totalItems={projects?.totalItems}
            onPageChange={handlePageChange}
            addClass="mt-4"
          />
        )}
      </div>
    );
  })
);

export default ProjectsTab;
